import { useEffect, useMemo, useRef, useState } from 'react';
import { Bell, MessageCircle, X } from 'lucide-react';
import { agoraISO, linkWhatsAppLembrete } from '../../utils/barbeariaLogica';
import type { BarbeariaEstado } from '../../utils/barbeariaTipos';

type TipoLembrete = '1d' | '1h';

interface Pendente {
  agendamentoId: string;
  tipo: TipoLembrete;
  nome: string;
  telefone: string;
  dataISO: string;
  hora: string;
  servico: string;
  link: string;
}

interface Props {
  estado: BarbeariaEstado;
  patch: (fn: (prev: BarbeariaEstado) => BarbeariaEstado) => void;
}

function listarPendentes(estado: BarbeariaEstado, agora: number): Pendente[] {
  const out: Pendente[] = [];
  for (const a of estado.agendamentos) {
    if (!a.lembreteWhatsApp) continue;
    if (a.status === 'cancelado' || a.status === 'concluido') continue;
    const cli = estado.clientes.find((c) => c.id === a.clienteId);
    const srv = estado.servicos.find((s) => s.id === a.servicoId);
    if (!cli?.telefone) continue;

    const base = {
      agendamentoId: a.id,
      nome: cli.nome,
      telefone: cli.telefone,
      dataISO: a.dataISO,
      hora: a.horaInicio,
      servico: srv?.nome || 'serviço',
      link: linkWhatsAppLembrete(
        cli.telefone,
        cli.nome,
        a.dataISO,
        a.horaInicio,
        srv?.nome || 'serviço',
        estado.config.nomeLoja
      ),
    };

    const diaPara = a.lembrete1DiaPara ? Date.parse(a.lembrete1DiaPara) : NaN;
    if (Number.isFinite(diaPara) && diaPara <= agora && !a.lembrete1DiaDisparadoEm) {
      out.push({ ...base, tipo: '1d' });
    }

    const horaPara = a.lembrete1HoraPara
      ? Date.parse(a.lembrete1HoraPara)
      : a.lembreteAgendadoPara
        ? Date.parse(a.lembreteAgendadoPara)
        : NaN;
    if (
      Number.isFinite(horaPara) &&
      horaPara <= agora &&
      !a.lembrete1HoraDisparadoEm &&
      !a.lembreteDisparadoEm
    ) {
      out.push({ ...base, tipo: '1h' });
    }
  }
  return out;
}

function chavePendente(p: Pendente): string {
  return `${p.agendamentoId}-${p.tipo}`;
}

/** Sininho discreto + balão rápido (não ocupa a tela). */
export default function BarbeariaLembretesWhatsApp({ estado, patch }: Props) {
  const [tick, setTick] = useState(0);
  const [aberto, setAberto] = useState(false);
  const [balao, setBalao] = useState<string | null>(null);
  const avisadosRef = useRef<Set<string>>(new Set());
  const balaoTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 20000);
    return () => window.clearInterval(id);
  }, []);

  const pendentes = useMemo(
    () => listarPendentes(estado, Date.now()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [estado, tick]
  );

  // Balão rápido só para lembretes novos (some em ~3s)
  useEffect(() => {
    const novos = pendentes.filter((p) => !avisadosRef.current.has(chavePendente(p)));
    if (novos.length === 0) return;
    for (const p of novos) avisadosRef.current.add(chavePendente(p));

    const primeiro = novos[0];
    const tipo = primeiro.tipo === '1d' ? '1 dia antes' : '1 hora antes';
    const texto =
      novos.length === 1
        ? `Lembrete pendente: ${primeiro.nome} (${tipo})`
        : `${novos.length} lembretes precisam ser enviados`;

    setBalao(texto);
    if (balaoTimerRef.current) window.clearTimeout(balaoTimerRef.current);
    balaoTimerRef.current = window.setTimeout(() => {
      setBalao(null);
      balaoTimerRef.current = null;
    }, 3200);

    return () => {
      if (balaoTimerRef.current) window.clearTimeout(balaoTimerRef.current);
    };
  }, [pendentes]);

  const marcarEnviado = (p: Pendente) => {
    const agora = agoraISO();
    patch((prev) => ({
      ...prev,
      agendamentos: prev.agendamentos.map((a) => {
        if (a.id !== p.agendamentoId) return a;
        if (p.tipo === '1d') {
          return { ...a, lembrete1DiaDisparadoEm: agora, atualizadoEm: agora };
        }
        return {
          ...a,
          lembrete1HoraDisparadoEm: agora,
          lembreteDisparadoEm: agora,
          atualizadoEm: agora,
        };
      }),
      atualizadoEm: agora,
    }));
  };

  const enviar = (p: Pendente) => {
    try {
      window.open(p.link, '_blank');
    } catch {
      /* ignore */
    }
    marcarEnviado(p);
  };

  const qtd = pendentes.length;
  if (qtd === 0 && !balao && !aberto) return null;

  return (
    <div className="fixed bottom-4 right-3 z-[90] flex flex-col-reverse items-end gap-2 sm:bottom-5 sm:right-4">
      {/* Balão que some rápido */}
      {balao && (
        <div
          role="status"
          className="max-w-[min(18rem,calc(100vw-4.5rem))] animate-[bbFadeIn_0.2s_ease-out] rounded-2xl border border-amber-800/40 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-950 shadow-lg"
        >
          {balao}
          <span className="mt-0.5 block text-[10px] font-medium text-amber-800/80">
            Toque no sininho para enviar
          </span>
        </div>
      )}

      <div className="relative">
        <button
          type="button"
          onClick={() => setAberto((v) => !v)}
          className="relative flex h-11 w-11 items-center justify-center rounded-full border-2 border-neutral-800 bg-white text-neutral-900 shadow-md hover:bg-amber-50"
          title={qtd > 0 ? `${qtd} lembrete(s) pendente(s)` : 'Lembretes WhatsApp'}
          aria-label="Lembretes WhatsApp"
        >
          <Bell className={`h-5 w-5 ${qtd > 0 ? 'text-amber-700' : 'text-neutral-600'}`} />
          {qtd > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-black text-white">
              {qtd > 9 ? '9+' : qtd}
            </span>
          )}
        </button>

        {aberto && (
          <div className="absolute bottom-14 right-0 w-[min(20rem,calc(100vw-1.5rem))] rounded-2xl border-2 border-neutral-800 bg-white p-3 shadow-xl">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-xs font-black uppercase tracking-wide text-neutral-800">
                Lembretes {qtd > 0 ? `(${qtd})` : ''}
              </p>
              <button
                type="button"
                onClick={() => setAberto(false)}
                className="rounded-lg p-1 text-neutral-500 hover:bg-neutral-100"
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {qtd === 0 ? (
              <p className="text-xs text-neutral-600">Nenhum lembrete pendente agora.</p>
            ) : (
              <ul className="max-h-56 space-y-2 overflow-y-auto">
                {pendentes.map((p) => (
                  <li
                    key={chavePendente(p)}
                    className="rounded-xl border border-neutral-300 bg-neutral-50 px-2.5 py-2 text-xs"
                  >
                    <p className="font-bold text-neutral-900">
                      {p.nome} · {p.tipo === '1d' ? '1 dia antes' : '1 hora antes'}
                    </p>
                    <p className="text-neutral-600">
                      {p.dataISO.split('-').reverse().join('/')} às {p.hora} · {p.servico}
                    </p>
                    <button
                      type="button"
                      onClick={() => enviar(p)}
                      className="mt-1.5 inline-flex items-center gap-1 rounded-lg border border-emerald-800/40 bg-emerald-600 px-2.5 py-1.5 font-bold text-white"
                    >
                      <MessageCircle className="h-3.5 w-3.5" />
                      Enviar no WhatsApp
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
